import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './styles';
import BananatagButton from '../../../componentLibrary/BananatagButton';
import Checkbox from '../../../componentLibrary/Checkbox';
import handleKeyPress from '../../../common/handleKeyPress';

/**
 * Returns what the array of users was before the component updated.
 * Used to check if the amount of users went up or down.
 * @param {array} users Array of connected users
 */
const usePreviousUsers = (users) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = users;
    }, [users]);
    return ref.current || [];
};

const MultipleUsersModal = ({ connectedClients, multipleUsersConnected, userPlan }) => {
    const [showMultipleUsersModal, setShowMultipleUsersModal] = useState(true);
    const [modalReappear, setModalReappear] = useState(true);
    const [checked, setChecked] = useState(false);
    const prevUsers = usePreviousUsers(connectedClients);

    useLayoutEffect(() => {
        const usersIncreased = connectedClients.length > prevUsers.length;
        if (modalReappear && usersIncreased) {
            setShowMultipleUsersModal(multipleUsersConnected);
        }
    }, [connectedClients]);

    const hideModal = () => {
        setModalReappear(!checked);
        setShowMultipleUsersModal(false);
    };

    const toggleModalReappearing = () => {
        setChecked(!checked);
    };

    if (userPlan !== 'IC_LEGACY') {
        return null;
    }

    if (multipleUsersConnected && modalReappear && showMultipleUsersModal) {
        return (
            <Styled.Background>
                <Styled.ModalBackground>
                    <Styled.HeaderText>Multiple editors in this draft</Styled.HeaderText>
                    <Styled.TextWrapper>
                        This may mean:
                        <Styled.ReasonsList>
                            <li>You&apos;re editing in another tab or window</li>
                            <li>Somebody else is editing it</li>
                        </Styled.ReasonsList>
                        <p>Please make sure to only edit one instance at a time. Otherwise, changes may be lost.</p>
                        <Styled.UpgradeBox>
                            <Styled.FAIcon icon="magic" />
                            <p>
                                If your team prefers to work simultaneously (like Google Docs), our Essential, Power,
                                and Enterprise plans offer&nbsp;
                                <a
                                    href="https://staffbase.com/en/employee-newsletter/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Real-time Editing.
                                </a>
                            </p>
                        </Styled.UpgradeBox>
                    </Styled.TextWrapper>
                    <Styled.CheckboxWrapper>
                        <Checkbox
                            color={checked ? '#0277BD' : '#979799'}
                            icon={checked ? 'check_box' : 'check_box_outline_blank'}
                            isChecked={checked}
                            labelId="dismiss-modal-reappearing-label"
                            onClick={toggleModalReappearing}
                            onKeyPress={(e) => handleKeyPress(e, toggleModalReappearing)}
                        />
                        <Styled.CheckboxText id="dismiss-modal-reappearing-label">
                            Don&apos;t show again in this session
                        </Styled.CheckboxText>
                    </Styled.CheckboxWrapper>
                    <Styled.ButtonWrapper>
                        <BananatagButton size="large" text="PROCEED" onClick={hideModal} styling={{ marginLeft: 8 }} />
                    </Styled.ButtonWrapper>
                </Styled.ModalBackground>
            </Styled.Background>
        );
    }
    return null;
};

MultipleUsersModal.propTypes = {
    connectedClients: PropTypes.array.isRequired,
    multipleUsersConnected: PropTypes.bool.isRequired,
    userPlan: PropTypes.string.isRequired,
};

export default MultipleUsersModal;
